import React, { useState } from 'react';
import { FlatList } from 'react-native';
import { Checkbox } from '../../Components';
import type { PlayerBalanceProps, PlayerSquareProps, SquareProps } from '../../types';
import BidToggle from './BidToggle';
import { Button, Text, View } from '../../Components/Themed';
import { useColors } from '../../constants/useColors';

type BidFormProps = {
    player_id?:string,
    market_type:string,
    home_abbr?:string,
    width:number,
    player_balance?:PlayerBalanceProps,
    away_abbr?:string,
    submit_ready: { is_loading:boolean, is_ready:Boolean }
    onSubmitBid: (promo_balance?:boolean) => void,
    onClearBids: () => void,
    onBidAmountChange: (player_bid:PlayerSquareProps, square:SquareProps, direction: 'increase'|'decrease') => void,
    onRequestAuthenticate: () => void,
    square_bids:PlayerSquareProps[],
    squares:SquareProps[]
}

const form_sections = ['header','bids','actions']

const BidForm = ({ player_id, player_balance, market_type, home_abbr, away_abbr, submit_ready, square_bids, squares, onSubmitBid, onClearBids, onBidAmountChange, onRequestAuthenticate }:BidFormProps) => {
    const Colors = useColors();
    const [ promo_balance, setPromoBalance ] = useState(false);
    const cl = market_type == 'FOR_MONEY' ? '$' : 'E'
    const total_bid_amount = square_bids.reduce((a,b) => a + b.purchase_price, 0)

    let can_submit = player_balance && total_bid_amount < player_balance.balance ? true : false
    if(player_balance && promo_balance && player_balance.promo_balance > total_bid_amount){ can_submit = true }

    const renderBids = (data: { item:PlayerSquareProps, index:number }) => {
        let square = squares.find(s => s.sq_square_id == data.item.sq_square_id)
        if(!square){ return ( <></> ) }
        return (
            <View type='row' transparent style={{ borderBottomWidth:1, borderColor:Colors.borders.light, padding:5}}>
                <View transparent style={{flex:1}}>
                    <Text theme='h2'>SQUARE: {home_abbr}: {square.square_score_x}, {away_abbr}: {square.square_score_y}</Text>
                    <Text theme="description">Current Bid: {cl}{square.last_purchase_price.toFixed(2)}</Text>
                </View>
                <View transparent>
                    <BidToggle sq_square_id={data.item.sq_square_id} amount={data.item.purchase_price} cl={cl}  onAmountChange={(dir) => onBidAmountChange(data.item, square, dir)}/>
                </View>
            </View>
        )
    }

    const renderSections = (data:{ item:string, index:number }) => {
        switch(data.item){
            case 'header':
                return (
                <View type='header' style={{ flexDirection:'row', alignItems:'center', padding:10, borderTopRightRadius:8, borderTopLeftRadius:8 }}>
                    <View transparent style={{ flex:1 }}>
                        <Text theme="h1">SET BID AMOUNTS</Text>
                        <Text style={{ marginTop:3 }} color={Colors.text.error} size={12} weight='regular'>All purchases are final and cannot be removed</Text>
                    </View>
                    <View float style={{ height:24, width:24, borderRadius:100, justifyContent:'center', alignItems:'center', marginLeft: 5 }}>
                        <Text size={12} theme='h1' textAlign='center'>{square_bids.length}</Text>
                    </View>
                </View>
                )
            case 'bids':
                return (
                <View type='body' transparent style={{ padding:20, flexGrow:1 }}>
                    <FlatList data={square_bids.sort((a,b) => parseInt(a.sq_square_id) - parseInt(b.sq_square_id))} renderItem={renderBids} keyExtractor={(item) => `b-${item.sq_square_id}`}/>
                    <View transparent type='row' style={{ marginTop:5 }}>
                        <Text style={{ flex:1 }} theme='description'>Total</Text>
                        <Text theme='h2'>{cl}{total_bid_amount.toFixed(2)}</Text>
                    </View>
                    {player_balance && player_balance.promo_balance > 0 ?
                    <Button transparent style={{ flexDirection:'row', alignItems:'center', margin:5, padding:5, backgroundColor:Colors.text.gold, borderRadius:8 }} onPress={() => setPromoBalance(!promo_balance)}>
                        <Checkbox
                            checked={promo_balance}
                            disabled
                            size={26}
                            onSelect={() => setPromoBalance(!promo_balance)}
                        />
                        <View transparent style={{ flex:1, marginLeft:10 }}>
                            <Text size={12} color={Colors.text.white} weight='bold'>${player_balance.promo_balance.toFixed(2)} Promotion Available</Text>
                            <Text size={10} color={Colors.text.white} weight='regular' style={{ marginTop:3 }}>Press / Click here to use promo balance</Text>
                        </View>
                    </Button>
                    :<></>}
                </View>
                )
            
            default: return <></>
        }
    }
 
    return (
        <View float style={{ padding:0, maxHeight:550, maxWidth:600 }}>
            <FlatList
                key='bid_form_sections'
                data={form_sections}
                renderItem={renderSections}
                keyExtractor={(item) => item}
            />
            <View type='footer' style={{ flexDirection:'row', alignItems:'center', padding:10, borderBottomRightRadius:8, borderBottomLeftRadius:8 }}>                
                <Button 
                    type='error'
                    title='CLEAR'
                    style={{ flex:1 }} onPress={() => onClearBids()}>
                </Button>
                {player_id ?
                <Button 
                    disabled={!can_submit || submit_ready.is_loading}
                    loading={submit_ready.is_loading}
                    style={{ flex:2, opacity:submit_ready.is_loading || !can_submit ? 0.5:1, marginLeft:5, backgroundColor:submit_ready.is_ready?Colors.text.success:Colors.text.action }} 
                    type='success'
                    title={submit_ready.is_ready?'SUBMIT OFFER':'REVIEW OFFER'}
                    onPress={() => onSubmitBid(promo_balance)}
                />
                :
                <Button 
                    style={{ flex:2, marginLeft:5 }}
                    type='success'
                    onPress={() => onRequestAuthenticate()}>
                    <Text size={14} weight='semibold' textAlign='center' color={Colors.text.white}>Sign Up</Text>
                </Button>
                }
            </View>
        </View>
    )
}

export default BidForm